import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0 } from "../../lib/format";
import { Modal, Notice, Stat } from "../../lib/ui";
import "./inventory.css";

/*
 * Top products & stock levels – port of availabilty.php (API: GET /api/stock/availability, backend/stock.py)
 * Stock levels: critical ≤ minimum; low ≤ 2 × minimum; good > 2 × minimum.
 * Select two or more products to compare sales, profit, units sold and stock side by side.
 */

const LEVEL = (p) => (p.quantity > p.minimum_stock * 2 ? "good" : p.quantity > p.minimum_stock ? "low" : "critical");
const LEVEL_LABEL = { good: "Good stock", low: "Low stock", critical: "Critical" };
const COLORS = ["#8b1a1a", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6"];

function CompareBars({ title, products, value, extra }) {
  const max = Math.max(1, ...products.map((p) => Math.max(value(p), extra ? extra(p) : 0)));
  return (
    <div className="av-chart">
      <h3>{title}</h3>
      {products.map((p, i) => (
        <div key={p.id} className="av-bar-row">
          <span className="av-bar-label" title={p.name}>{p.name}</span>
          <div className="av-bar-track">
            <div className="av-bar" style={{ width: `${(value(p) / max) * 100}%`, background: COLORS[i % COLORS.length] }} />
            {extra && <div className="av-bar-min" style={{ left: `${(extra(p) / max) * 100}%` }} title={`Minimum: ${n0(extra(p))}`} />}
          </div>
          <span className="av-bar-val mono">{n0(value(p))}</span>
        </div>
      ))}
    </div>
  );
}

export default function Availability() {
  const api = useApi();
  const [f, setF] = useState({ stock_filter: "all", sort_by: "sales", limit: 20 });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [compare, setCompare] = useState(false);

  const load = useCallback(async (q) => {
    try { setData(await api.get("/api/stock/availability", q)); setError(null); setSel(new Set()); }
    catch (e) { setError(e.message); }
  }, [api]);
  useEffect(() => { load(f); }, [load, f]);

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectWhere = (pred) => setSel(new Set((data?.products || []).filter(pred).map((p) => p.id)));
  const chosen = (data?.products || []).filter((p) => sel.has(p.id));

  return (
    <main className="pg">
      <PageHeader slug="availabilty" />
      <Notice notice={error && { tone: "err", text: error }} />
      {data && (
        <>
          <div className="ui-stats">
            <Stat icon="fa-boxes-stacked" label="Total products" value={n0(data.stats.total_products)} />
            <Stat icon="fa-triangle-exclamation" tone="danger" label="Critical stock" value={n0(data.stats.critical_count)} />
            <Stat icon="fa-circle-exclamation" tone="warn" label="Low stock" value={n0(data.stats.low_count)} />
            <Stat icon="fa-circle-check" tone="up" label="Good stock" value={n0(data.stats.good_count)} />
          </div>
          {data.stats.critical_count > 0 && f.stock_filter === "all" && (
            <div className="ui-notice err">
              <span>{data.stats.critical_count} product{data.stats.critical_count === 1 ? " is" : "s are"} at or below minimum stock.</span>
              <button type="button" className="btn sm" onClick={() => setF({ ...f, stock_filter: "critical" })}>View critical items</button>
            </div>
          )}
        </>
      )}

      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <div className="toolbar">
          <div className="field"><label htmlFor="av-f">Stock filter</label>
            <select id="av-f" value={f.stock_filter} onChange={(e) => setF({ ...f, stock_filter: e.target.value })}>
              <option value="all">All products</option><option value="critical">Critical (at or below minimum)</option>
              <option value="low">Low stock</option><option value="below_minimum">Below minimum only</option><option value="good">Good stock</option>
            </select></div>
          <div className="field"><label htmlFor="av-s">Sort by</label>
            <select id="av-s" value={f.sort_by} onChange={(e) => setF({ ...f, sort_by: e.target.value })}>
              <option value="sales">Highest sales</option><option value="profit">Highest profit</option>
              <option value="quantity">Most items sold</option><option value="stock">Lowest stock first</option>
            </select></div>
          <div className="field"><label htmlFor="av-l">Show top</label>
            <select id="av-l" value={f.limit} onChange={(e) => setF({ ...f, limit: Number(e.target.value) })}>
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} products</option>)}
            </select></div>
          <div className="av-select">
            <button type="button" className="btn sm" onClick={() => selectWhere(() => true)}>Select all</button>
            <button type="button" className="btn sm" onClick={() => selectWhere((p) => LEVEL(p) === "low")}>Select low</button>
            <button type="button" className="btn sm" onClick={() => selectWhere((p) => LEVEL(p) === "critical")}>Select critical</button>
            <button type="button" className="btn sm" onClick={() => setSel(new Set())} disabled={!sel.size}>Clear</button>
            <button type="button" className="btn primary sm" disabled={sel.size < 2} onClick={() => setCompare(true)}>
              <i className="fas fa-chart-column" /> Compare {sel.size > 0 && <span className="mono">({sel.size})</span>}</button>
          </div>
        </div>
        {data && (
          <p className="av-count">Showing {data.products.length} product{data.products.length === 1 ? "" : "s"}
            {f.stock_filter !== "all" && ` · ${f.stock_filter.replace("_", " ")} filter on`}. Click cards to select them for comparison.</p>
        )}
      </section>

      <div className="av-grid">
        {data?.products.map((p) => {
          const lvl = LEVEL(p);
          const on = sel.has(p.id);
          return (
            <button key={p.id} type="button" className={`av-card ${lvl} ${on ? "on" : ""}`} onClick={() => toggle(p.id)} aria-pressed={on}>
              <span className="av-top"><span className="av-name">{p.name}</span><i className={`fas ${on ? "fa-square-check" : "fa-square"}`} /></span>
              <span className="av-mfr">{p.manufacturer || "No manufacturer"}</span>
              <span className={`pill-tag ${lvl === "good" ? "ok" : lvl === "low" ? "warn" : "err"}`}>{LEVEL_LABEL[lvl]}</span>
              <dl>
                <div><dt>Stock</dt><dd className="mono">{n0(p.quantity)}</dd></div>
                <div><dt>Minimum</dt><dd className="mono">{n0(p.minimum_stock)}</dd></div>
                <div><dt>Sold</dt><dd className="mono">{n0(p.qty_sold)}</dd></div>
                <div><dt>Sales (TZS)</dt><dd className="mono">{n0(p.total_sales)}</dd></div>
                <div><dt>Profit (TZS)</dt><dd className="mono">{n0(p.total_profit)}</dd></div>
              </dl>
            </button>
          );
        })}
      </div>
      {data && data.products.length === 0 && <div className="panel empty"><i className="fas fa-magnifying-glass" />No products found. Try adjusting your filters.</div>}

      {compare && (
        <Modal title="Product comparison" onClose={() => setCompare(false)} actions={<button type="button" className="btn" onClick={() => setCompare(false)}>Close</button>}>
          <div className="av-modal">
            <div className="tbl-wrap">
              <table className="tbl">
                <thead><tr><th>Product</th><th>Manufacturer</th><th className="num">Stock</th><th className="num">Min</th><th className="num">Sold</th>
                  <th className="num">Sales</th><th className="num">Profit</th><th className="num">Margin</th></tr></thead>
                <tbody>{chosen.map((p) => (
                  <tr key={p.id}><td style={{ fontWeight: 600 }}>{p.name}</td><td>{p.manufacturer}</td>
                    <td className="num mono">{n0(p.quantity)} <span className={`pill-tag ${LEVEL(p) === "good" ? "ok" : LEVEL(p) === "low" ? "warn" : "err"}`}>{LEVEL(p)}</span></td>
                    <td className="num mono">{n0(p.minimum_stock)}</td><td className="num mono">{n0(p.qty_sold)}</td>
                    <td className="num mono">{n0(p.total_sales)}</td><td className="num mono">{n0(p.total_profit)}</td>
                    <td className="num mono">{p.total_sales > 0 ? ((p.total_profit / p.total_sales) * 100).toFixed(2) : "0"}%</td></tr>
                ))}</tbody>
              </table>
            </div>
            <div className="av-charts">
              <CompareBars title="Sales (TZS)" products={chosen} value={(p) => p.total_sales} />
              <CompareBars title="Profit (TZS)" products={chosen} value={(p) => p.total_profit} />
              <CompareBars title="Units sold" products={chosen} value={(p) => p.qty_sold} />
              <CompareBars title="Stock level (line = minimum)" products={chosen} value={(p) => p.quantity} extra={(p) => p.minimum_stock} />
            </div>
          </div>
        </Modal>
      )}
    </main>
  );
}
